import { SnkPriceDescriptor, SnkProductPricing } from 'graphql/models/generated';
import { OfferPricing, ElementPricing, PriceDescriptor } from 'models/pricing.model';

export function getPriceDescriptor (
  pricing: SnkProductPricing | OfferPricing,
  key = '',
  index?: number
): SnkPriceDescriptor | undefined {
  let pricingData;

  if (index !== undefined) {
    const price = pricing as SnkProductPricing;
    pricingData = (price.elements?.length && price.elements[index]
      ? (price.elements[index]) as { [key: string]: string }
      : undefined);
  } else {
    pricingData = pricing as { [key: string]: string };
  }

  return pricingData ? pricingData[key] as SnkPriceDescriptor : undefined;
}

export function getPriceKeyByStepId(pricing: pricingObjectDataInput, stepId: string) {
  return Object.keys(pricing).find(
    key => pricing[key]?.stepId && pricing[key].stepId === stepId
  );
}

export function setPriceRateByStepId(
  pricing: pricingObjectDataInput,
  stepId: string,
  rate: number | string,
  changedManually?: boolean
): pricingObjectDataInput {
  const priceKey = getPriceKeyByStepId(pricing, stepId);

  if (priceKey) {
    return {
      ...pricing,
      ...setPricingObjectValue(pricing, priceKey, rate, changedManually)
    };
  }

  return pricing;
}

export function setElementPriceRateByStepId(
  element: ElementPricing,
  stepId: string,
  rate: number | string
): ElementPricing {
  const pricing = element as pricingObjectDataInput;
  const priceKey = getPriceKeyByStepId(pricing, stepId);

  if (priceKey) {
    return {
      ...pricing,
      ...setPricingObjectValue(pricing, priceKey, rate, true)
    } as ElementPricing;
  }

  return pricing as ElementPricing;
}

export function setElementPriceRateByKey(
  element: ElementPricing,
  key: string,
  rate: number | string,
  resetChangedManually?: boolean
): ElementPricing {
  const pricing = element as pricingObjectDataInput;

  return {
    ...pricing,
    ...setPricingObjectValueChanged(pricing, key, rate, resetChangedManually)
  } as ElementPricing;
}

export function updateLineElementsPricingByKey(
  elements: ElementPricing[],
  key: string,
  rate: number | string,
  resetChangedManually?: boolean
) {
  const lines: ElementPricing[] = [];
  elements.forEach((element) => {
    const pricing = setElementPriceRateByKey(element, key, rate, resetChangedManually);
    lines.push(pricing);
  });

  return lines;
}

export function mergeLineElementsPricing(
  elements: ElementPricing[],
  stepId: string,
  rate: number | string
) {
  const lines: ElementPricing[] = [];
  elements.forEach((element) => {
    const pricing = setElementPriceRateByStepId(element, stepId, rate);
    lines.push(pricing);
  });

  return lines;
}

export function setPricingObjectValue(
  input: pricingObjectDataInput,
  key: string,
  rate: number | string,
  changedManually?: boolean
): any {
  if (!input || !input[key] || !key || rate === undefined) {
    return undefined;
  }

  let updatedValue = { ...input[key], value: Number(rate) };

  if (changedManually !== undefined) {
    updatedValue = { ...updatedValue, isChangedManually: changedManually}
  }

  return {
    ...input,
    ...{
      [key]: updatedValue as PriceDescriptor
    }
  };
}

export function setPricingObjectValueChanged(
  input: pricingObjectDataInput,
  key: string,
  rate: number | string,
  updateChangedManually = false
): any {
  if (!input || !input[key] || !key || rate === undefined) {
    return undefined;
  }
  if (!input[key].isChangedManually) {
    return setPricingObjectValue(input, key, rate);
  }
  if (updateChangedManually && input[key].isChangedManually) {
    return setPricingObjectValue(input, key, rate, false);
  }

  return input;
}

interface pricingObjectDataInput {
  [key: string]: any;
}
